// File: src/pages/TeamOKRs.js

import React, { useState, useEffect, useContext, useMemo } from 'react'
import './MyOKRs.css'
import '../styles/okr-m3-tokens.css'
import { authFetch } from '../utils/api'
import { AuthContext } from '../context/AuthContext'
import { calculateOKRStatus } from '../utils/okrStatusUtils';
import MetricsBar from '../components/okr/MetricsBar'
import StatusFilterChips from '../components/okr/StatusFilterChips'
import ObjectiveCard from '../components/okr/ObjectiveCard'

export default function TeamOKRs() {
  const { user } = useContext(AuthContext)
  const [okrs, setOkrs]               = useState([])
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState(null)
  const [expandedMap, setExpandedMap] = useState({})
  const [selectedStatuses, setSelectedStatuses] = useState([])

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchTeamOKRs();
  }, [user]);

  const fetchTeamOKRs = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      // Fetch all OKRs, then filter for team-level OKRs
      const res = await authFetch('/api/okrs', token);
      
      if (!res.ok) {
        const errorText = await res.text().catch(() => 'Unknown error');
        throw new Error(`HTTP error! status: ${res.status} - ${errorText}`);
      }
      
      const data = await res.json();
      
      // Ensure we have an array
      if (!Array.isArray(data)) {
        console.error('Expected array but got:', typeof data, data);
        setOkrs([]);
        setLoading(false);
        return;
      }
      
      console.log('🔍 All OKRs fetched for team filtering:', data.length, 'total OKRs');
      
      // Filter to only team-level OKRs
      const teamOKRs = data.filter(o => {
        // Check if it's a team-level OKR
        const isTeamLevel = o.level === 'team';
        
        // Check if user is part of this team (various ways)
        const userInTeam = 
          // Numeric teamId match
          (o.teamId && user.teamId && o.teamId.toString() === user.teamId.toString()) ||
          // String-based team name match
          (o.team && user.team && o.team === user.team) ||
          // User is in the owners array
          (Array.isArray(o.owners) && o.owners.includes(user.name)) ||
          // User is the owner
          (o.owner === user.name) ||
          // User's department matches team
          (o.team && user.department && o.team === user.department);
        
        console.log(`🔍 OKR "${o.objective}": level=${o.level}, team=${o.team}, owners=${o.owners}, user.team=${user.team}, user.department=${user.department}`);
        console.log(`   Matches: teamLevel=${isTeamLevel}, userInTeam=${userInTeam}`);
        
        return isTeamLevel && userInTeam;
      });

      console.log(`✅ Filtered to ${teamOKRs.length} team OKRs for user ${user.name}`);
      
      // Initialize expand/collapse state
      const init = {};
      teamOKRs.forEach(o => { init[o._id] = false; });
      setExpandedMap(init);
      
      setOkrs(teamOKRs);
    } catch (err) {
      console.error('TeamOKRs fetch error:', err);
      setError(err.message || 'Failed to load team OKRs');
      setOkrs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = id =>
    setExpandedMap(m => ({ ...m, [id]: !m[id] }))
  
  const expandAll = () =>
    setExpandedMap(Object.fromEntries(okrs.map(o => [o._id, true])))
  const collapseAll = () =>
    setExpandedMap(Object.fromEntries(okrs.map(o => [o._id, false])))
  
  const handleToggleStatus = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };
  
  // Calculate overall stats
  const { overallProgress, overallScore, totalOkrs, totalKRs } = useMemo(() => {
    const totalOkrs = okrs.length;
    let totalKRs = 0, done = 0, sum = 0;
    okrs.forEach(o => {
      totalKRs += o.keyResults?.length || 0;
      done += o.keyResults?.filter(kr => kr.completed).length || 0;
      sum += o.score || 0;
    });
    const overallProgress = totalKRs ? Math.round((done / totalKRs) * 100) : 0;
    const overallScore = totalOkrs ? sum / totalOkrs : 0;
    return { overallProgress, overallScore, totalOkrs, totalKRs };
  }, [okrs]);
  
  // Before rendering, recalculate status for each OKR and filter
  const okrsWithStatus = useMemo(() => {
    const withStatus = okrs.map(okr => ({
      ...okr,
      status: calculateOKRStatus(okr.keyResults || [], okr.deadline)
    }));
    
    if (selectedStatuses.length === 0) return withStatus;
    return withStatus.filter(okr => selectedStatuses.includes(okr.status));
  }, [okrs, selectedStatuses]);

  if (loading) {
    return <div className="m3-center-message">Loading…</div>
  }
  if (error) {
    return <div className="m3-center-message m3-error">Error: {error}</div>
  }

  return (
    <div className="m3-my-okrs-page">
      <div className="m3-page-header">
        <h1 className="m3-page-title">My Team OKRs</h1>
      </div>

      {/* Metrics Bar */}
      {okrs.length > 0 && (
        <MetricsBar
          progress={overallProgress}
          score={overallScore}
          objectives={totalOkrs}
          keyResults={totalKRs}
        />
      )}

      {/* Status Filter Chips */}
      <StatusFilterChips
        selectedStatuses={selectedStatuses}
        onToggleStatus={handleToggleStatus}
      />

      {/* Expand/Collapse Controls */}
      {okrs.length > 0 && (
        <div className="m3-expand-controls">
          <button className="m3-button m3-button-text" onClick={expandAll}>
            Expand All
          </button>
          <button className="m3-button m3-button-text" onClick={collapseAll}>
            Collapse All
          </button>
        </div>
      )}

      {/* OKRs List */}
      <section className="m3-okrs-content">
        {okrsWithStatus.length === 0 ? (
          <div className="m3-empty-state">
            <p>You have no team OKRs yet{selectedStatuses.length > 0 ? ' matching selected filters' : ''}.</p>
          </div>
        ) : (
          <div className="m3-okrs-list">
            {okrsWithStatus.map(okr => (
              <ObjectiveCard
                key={okr._id}
                okr={okr}
                isExpanded={expandedMap[okr._id] || false}
                onToggleExpand={toggleExpand}
                canEdit={false}
                canDelete={false}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
